import * as React from "react";

import { cn } from "@/lib/utils";

export interface InputProps extends React.ComponentProps<"input"> {
  variant?: "default" | "terminal";
}

/**
 * Input Component - Design System Atmosférico v3.0
 * 
 * Variants:
 * - default: UI padrão com micro radius
 * - terminal: Estilo terminal (JetBrains Mono, sharp corners)
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant = "default", ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          // Base styles
          "flex h-14 w-full px-4 py-2 text-base transition-all duration-instant",
          "ring-offset-void-absolute placeholder:text-textMuted",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          // Variant: default
          variant === "default" && [
            "rounded-micro bg-void-deep border border-void-warm text-textPrimary",
            "hover:border-gold-soft/30",
            "focus:border-gold-primary",
          ],
          // Variant: terminal (Prompt style)
          variant === "terminal" && [
            "rounded-sharp bg-void-deep border border-textMuted text-textPrimary",
            "font-mono caret-gold-primary",
            "focus:border-gold-primary focus:shadow-[0_0_0_1px_rgba(255,215,0,0.12)]",
          ],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

/**
 * PromptInput - Input específico para prompts de IA
 * Terminal style com caret dourado
 */
const PromptInput = React.forwardRef<HTMLInputElement, Omit<InputProps, "variant">>(
  ({ className, ...props }, ref) => (
    <Input
      ref={ref}
      variant="terminal"
      className={cn("font-mono text-textPrimary", className)}
      {...props}
    />
  )
);
PromptInput.displayName = "PromptInput";

export { Input, PromptInput };
